import { loadingScreen } from '@app/components/Loading';
import { IS_EXTERNAL, VITE_API_ENDPOINT, VITE_EXTERNAL_API_ENDPOINT } from '@app/consts/env_consts';
import { AppSettings } from '@settings/application-setting';
import axios, { AxiosHeaders } from 'axios';
import { updateAuthorizationHeader } from '../interceptors/AuthInterceptor';
import { httpResponseHandler, normalizeErrorResponse, normalizeResponse } from '../interceptors/ResponseInterceptor';

const httpClient = axios.create({
  baseURL: IS_EXTERNAL ? VITE_EXTERNAL_API_ENDPOINT : VITE_API_ENDPOINT,
  timeout: AppSettings.REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
  },
});

httpClient.interceptors.request.use((config) => {
  if (!config.headers) {
    config.headers = new AxiosHeaders();
  }
  updateAuthorizationHeader(config.headers);

  // For FormData, remove Content-Type so the browser can set the correct
  // multipart/form-data boundary automatically.
  if (config.data instanceof FormData) {
    config.headers.delete('Content-Type');
  }

  return config;
});

httpClient.interceptors.response.use(
  (response) => normalizeResponse(response) as ControlledType,
  async (error) => {
    await loadingScreen.hardHideDelay();
    // When browser blocks response due to CORS, error.response is undefined
    // but error.status may still reflect the actual HTTP status (e.g. 401)
    if (!error.response && (error as any).status) {
      error.response = { status: (error as any).status, data: {} } as any;
    }

    let abortRequest = false;
    abortRequest = httpResponseHandler(error);

    if (abortRequest) {
      return Promise.reject(error);
    }

    return normalizeErrorResponse(error);
  }
);

export default httpClient;

/**
 * Axios instance for APS (View Design) API — uses VITE_APS_API_ENDPOINT as baseURL.
 */
export const apsHttpClient = axios.create({
  baseURL: import.meta.env.VITE_API_ENDPOINT || '',
  timeout: AppSettings.REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

apsHttpClient.interceptors.request.use((config) => {
  if (!config.headers) {
    config.headers = new AxiosHeaders();
  }
  updateAuthorizationHeader(config.headers);
  if (config.data instanceof FormData) {
    config.headers.delete('Content-Type');
  }
  return config;
});

apsHttpClient.interceptors.response.use(
  (response) => normalizeResponse(response) as ControlledType,
  async (error) => {
    await loadingScreen.hardHideDelay();
    if (!error.response && (error as any).status) {
      error.response = { status: (error as any).status, data: {} } as any;
    }
    const abortRequest = httpResponseHandler(error);
    if (abortRequest) {
      return Promise.reject(error);
    }
    return normalizeErrorResponse(error);
  }
);
